import { Controller } from '@hotwired/stimulus';
import { get } from "@rails/request.js";

export default class extends Controller {
  static targets = ['sidebar', 'select', 'content', 'drawer', 'main'];

  get drawerEl() {
    return this.element.querySelector('#drawer-navigation');
  }

  initialize() {
    this.clearAllActiveStates();

    const menu_tag = this.getFromSession('$menu.tag') || 'fleet';
    this.saveInSession('$menu.tag', menu_tag);
    get(`/menu?menu_tag=${menu_tag}`, { responseKind: 'turbo-stream' });

    this.updatePill(menu_tag);
    this.updateColorScheme(menu_tag);
  }

  connect() {
    this._frameHandler = this.handleFrameRender.bind(this);
    document.addEventListener('turbo:stream-render', this._frameHandler);
  }

  disconnect() {
    document.removeEventListener('turbo:stream-render', this._frameHandler);
    this.hideFlyout(true);
  }

  contentTargetConnected() {
    this.applyNavItems(this.isSidebarCollapsed());
    this.applyFlyoutListeners();
  }

  // ─── Reacts to SidebarComponent's own collapse/expand (sidebar-component:collapse@window) ──

  handleSidebarCollapse(event) {
    const { collapsed } = event.detail;
    this.applyNavItems(collapsed);
    if (collapsed) this.hideFlyout(true);
    this.applyFlyoutListeners();
  }

  handleFrameRender(event) {
    if (event.detail?.newStream?.target === 'side_menu') {
      // defer until after Turbo applies the stream action to the DOM
      requestAnimationFrame(() => {
        this.applyNavItems(this.isSidebarCollapsed());
        this.applyFlyoutListeners();
      });
    }
  }

  applyNavItems(collapsed) {
    if (!collapsed) return;
    const sideMenu = this.drawerEl?.querySelector('#side_menu');
    sideMenu?.querySelectorAll('[id^="dropdown-"]').forEach(dd => dd.classList.add('hidden'));
    sideMenu?.querySelectorAll('[data-sidebar-chevron]').forEach(c => c.classList.add('-rotate-90'));
  }

  // ─── Menu section switching ───────────────────────────────────────────────

  handleTag(tag) {
    if (tag) {
      this.saveInSession('$menu.tag', tag);
      return tag;
    }
    return this.getFromSession('$menu.tag') || null;
  }

  changeMenu(event) {
    const pill = document.getElementById('mode-toggle-pill');
    const currentTag = pill?.dataset.tag || 'fleet';
    const nextTag = currentTag === 'fleet' ? 'config' : 'fleet';

    this.handleTag(nextTag);
    get(`/menu?menu_tag=${nextTag}`, { responseKind: 'turbo-stream' });
    this.updatePill(nextTag);
    this.updateColorScheme(nextTag);

    const trackingBtn = this.element.querySelector('[data-tag="tracking"]');
    if (trackingBtn) trackingBtn.classList.toggle('hidden', nextTag === 'config');
    if (this.isSidebarCollapsed()) this.hideFlyout(true);
    this.applyFlyoutListeners();
  }

  updatePill(tag) {
    const pill = document.getElementById('mode-toggle-pill');
    if (!pill) return;
    pill.dataset.tag = tag;
    pill.querySelector('.fleet-pill-content')?.classList.toggle('hidden', tag !== 'fleet');
    pill.querySelector('.config-pill-content')?.classList.toggle('hidden', tag !== 'config');
  }

  clearAllActiveStates() {
    const drawer = this.drawerEl;
    if (drawer) drawer.classList.remove('fleet-active', 'config-active');
  }

  updateColorScheme(activeTag) {
    const drawer = this.drawerEl;
    if (!drawer) return;

    this.clearAllActiveStates();
    if (activeTag === 'fleet') drawer.classList.add('fleet-active');
    else if (activeTag === 'config') drawer.classList.add('config-active');
  }

  onClick(event) {
    const item = event.target;
    if (item.classList.contains('menu-item')) {
      if (item.dataset['href'] === 'javascript:void(0);') return;
      this.saveInSession('$menu.selected', JSON.stringify({ group: item.dataset['groupId'] }));
      window.location = item.dataset['href'];
      return;
    }
    this.saveInSession('$menu.selected', JSON.stringify({ group: item.dataset['groupId'], item: item.dataset['id'] }));
  }

  open(event) {
    if (this.isSidebarCollapsed()) return;
    const item = event.target;
    if (item instanceof SVGElement) return;
    this.showDropdown(item.dataset['id'] ? item : item.parentNode);
  }

  textDecoration() {
    const tag = this.getFromSession('$menu.selected');
    if (!tag) { this.applyFlyoutListeners(); return; }

    const selectedItem = JSON.parse(tag);
    if (!this.isSidebarCollapsed()) {
      const group = document.querySelector(`[data-group-id="${selectedItem.group}"]`);
      if (group) this.showDropdown(group);
    }

    const item = document.querySelector(`[data-id="${selectedItem.item}"]`);
    if (item) item.classList.add('underline');

    this.applyFlyoutListeners();
  }

  showDropdown(button) {
    const dropdown = document.getElementById(`dropdown-${button.dataset['id']}`);
    if (dropdown) dropdown.classList.toggle('hidden');
    button.querySelector('[data-sidebar-chevron]')?.classList.toggle('-rotate-90');
  }

  retractSidebar() {
    this.drawerEl?.querySelectorAll('[id^="dropdown-"]').forEach(dd => dd.classList.add('hidden'));
    this.drawerEl?.querySelectorAll('[data-sidebar-chevron]').forEach(c => c.classList.add('-rotate-90'));
  }

  saveInSession(tag, value) { localStorage.setItem(tag, value); }
  getFromSession(tag) { return localStorage.getItem(tag); }

  // ─── Collapsed flyout ─────────────────────────────────────────────────────

  isSidebarCollapsed() {
    return this.drawerEl?.dataset.collapsed === 'true';
  }

  applyFlyoutListeners() {
    const frame = document.getElementById('side_menu');
    if (!frame) return;
    const collapsed = this.isSidebarCollapsed();

    frame.querySelectorAll('button[data-action="click->menu#open"]').forEach(btn => {
      if (btn._flyoutEnter) {
        btn.removeEventListener('mouseenter', btn._flyoutEnter);
        btn.removeEventListener('mouseleave', btn._flyoutLeave);
        delete btn._flyoutEnter;
        delete btn._flyoutLeave;
      }
      if (collapsed) {
        btn._flyoutEnter = () => this.showFlyout(btn);
        btn._flyoutLeave = () => this.startHideFlyout();
        btn.addEventListener('mouseenter', btn._flyoutEnter);
        btn.addEventListener('mouseleave', btn._flyoutLeave);
      }
    });
  }

  showFlyout(button) {
    this.cancelHideFlyout();
    this.hideFlyout(true);

    const rect = button.getBoundingClientRect();
    const sidebarRight = this.drawerEl?.getBoundingClientRect().right ?? 84;
    const dropdown = document.getElementById(`dropdown-${button.dataset.id}`);
    if (!dropdown) return;
    const links = Array.from(dropdown.querySelectorAll('a'));
    if (!links.length) return;

    const panel = document.createElement('div');
    panel.setAttribute('data-menu-flyout', '');
    Object.assign(panel.style, {
      position: 'fixed', left: `${sidebarRight + 4}px`, top: `${rect.top}px`,
      zIndex: '9999', minWidth: '180px', background: '#1f2937',
      border: '1px solid #374151', borderRadius: '8px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.5)', padding: '4px 0', pointerEvents: 'auto',
    });

    const header = document.createElement('div');
    header.textContent = button.querySelector('span')?.textContent.trim() ?? '';
    Object.assign(header.style, {
      padding: '6px 12px', fontSize: '0.7rem', fontWeight: '600',
      textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af',
      borderBottom: '1px solid #374151', marginBottom: '4px',
    });
    panel.appendChild(header);

    links.forEach(link => {
      const row = document.createElement('a');
      if (link.dataset.turboFrame) row.dataset.turboFrame = link.dataset.turboFrame;
      row.href = link.href;
      row.textContent = link.textContent.trim();
      if (link.dataset.id) row.dataset.id = link.dataset.id;
      if (link.dataset.groupId) row.dataset.groupId = link.dataset.groupId;
      Object.assign(row.style, {
        display: 'block', padding: '7px 14px', fontSize: '0.82rem', color: '#f3f4f6',
        textDecoration: 'none', whiteSpace: 'nowrap', borderRadius: '4px',
        margin: '0 4px', cursor: 'pointer', transition: 'background 0.15s',
      });
      row.addEventListener('mouseenter', () => { row.style.background = 'rgba(255,255,255,0.08)'; });
      row.addEventListener('mouseleave', () => { row.style.background = ''; });
      row.addEventListener('click', () => {
        this.saveInSession('$menu.selected', JSON.stringify({ group: link.dataset.groupId, item: link.dataset.id }));
        requestAnimationFrame(() => this.hideFlyout(true));
      });
      panel.appendChild(row);
    });

    panel.addEventListener('mouseenter', () => this.cancelHideFlyout());
    panel.addEventListener('mouseleave', () => this.startHideFlyout());
    document.body.appendChild(panel);
    this._flyout = panel;
  }

  startHideFlyout() {
    this._flyoutHideTimer = setTimeout(() => this.hideFlyout(true), 150);
  }

  cancelHideFlyout() {
    clearTimeout(this._flyoutHideTimer);
  }

  hideFlyout(immediate = false) {
    if (!immediate) this.cancelHideFlyout();
    if (this._flyout) { this._flyout.remove(); this._flyout = null; }
  }
}
